import React, { PropsWithChildren, Ref, RefObject, useCallback, useContext, useImperativeHandle, useRef, useState } from 'react';
import { CustomText } from '@/components/CustomText';
import { KeyboardAvoidingView, Platform, RefreshControl, ScrollView, ScrollViewProps, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { Text } from 'react95-native-rabbl';
import { Link } from 'expo-router';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppUtils from '@/utilities/AppUtils';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
// @ts-ignore
import { RefreshControl as WebRefreshControl } from 'react-native-web-refresh-control';

export interface BasicScrollViewProps extends ScrollViewProps {
  center?: boolean,
  overrideStyle?: boolean,
  ref?: RefObject<ScrollView>,
  useRefreshComponent?: boolean,
};

const RefreshComponent = Platform.OS === 'web' ? WebRefreshControl : RefreshControl;

export function BasicScrollView({
  center = false,
  overrideStyle = false,
  useRefreshComponent = true,
  contentContainerStyle,
  children,
  ref = undefined,
  ...props
}: PropsWithChildren<BasicScrollViewProps>) : React.ReactNode {
  const [refreshing, setRefreshing] = useState(false);
  const dispatch = useDispatch();
  const domain = useSelector((state: RootState) => state.user.domain);
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);

  const onRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      const data: any = {};
      if (!isMainLogicalHost) {
        data.domain = domain;
      }
      const response = await AppUtilities.fetchOptionallyAuthorized(
        "BasicScrollView.onRefresh",
        "/rabbl/api/initialdata",
        "POST",
        data,
      );
      dispatch(setData(response));
    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e));
    } finally {
      setRefreshing(false);
    }
  }, [domain, isMainLogicalHost]);

  const styles: StyleProp<ViewStyle>[] = [center ? AppConstants.STYLES.basicScrollViewCenter : AppConstants.STYLES.basicScrollView];
  if (contentContainerStyle) {
    if (!overrideStyle) {
      styles.push(contentContainerStyle);
    } else {
      styles.splice(0, styles.length);
      styles.push(contentContainerStyle);
    }
  }

  return (
    <ScrollView
      contentContainerStyle={styles}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      ref={ref}
      {...(useRefreshComponent && {
        refreshControl: <RefreshComponent refreshing={refreshing} onRefresh={onRefresh} />
      })}
      {...props}
    >
      {children}
    </ScrollView>
  );
};
